import React, { useState, useEffect } from 'react';
import PublicHeader from '../../components/ui/PublicHeader';
import HeroSection from './components/HeroSection';
import RecommendationQuiz from './components/RecommendationQuiz';
import TrendingTopics from './components/TrendingTopics';
import SocialProof from './components/SocialProof';
import NewsletterSignup from './components/NewsletterSignup';
import Footer from './components/Footer';
import productService from '../../services/productService';

const Homepage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    try {
      setLoading(true);
      const products = await productService?.getAllProducts();
      // Get top 6 products for featured section
      setFeaturedProducts(products?.slice(0, 6));
    } catch (error) {
      console.error('Error loading featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <div className="pt-16">
        <HeroSection />
        
        {/* Featured Products Section */}
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                Featured Vacuum Reviews
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Expert-tested and user-reviewed vacuum cleaners to help you make the best choice
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)]?.map((_, i) => (
                  <div key={i} className="animate-pulse bg-white rounded-xl p-6">
                    <div className="aspect-square bg-muted rounded-lg mb-4" />
                    <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredProducts?.map((product) => (
                  <a
                    key={product?.id}
                    href={`/product-review/${product?.slug}`}
                    className="group bg-white rounded-xl shadow-subtle border border-border hover:shadow-medium transition-all duration-300 overflow-hidden"
                  >
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product?.imageUrl}
                        alt={product?.imageAlt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-primary">{product?.brand}</span>
                        {product?.badge && (
                          <span className="text-xs px-2 py-1 bg-accent/10 text-accent rounded-full">
                            {product?.badge?.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-text-primary mb-2 group-hover:text-primary transition-colors">
                        {product?.name}
                      </h3>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center">
                          {[...Array(5)]?.map((_, i) => (
                            <span
                              key={i}
                              className={`text-sm ${
                                i < Math.floor(product?.rating)
                                  ? 'text-accent' :'text-muted-foreground'
                              }`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {product?.rating} ({product?.reviewCount} reviews)
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-2xl font-bold text-text-primary">
                            ${product?.currentPrice}
                          </span>
                          {product?.originalPrice && (
                            <span className="text-sm text-muted-foreground line-through ml-2">
                              ${product?.originalPrice}
                            </span>
                          )}
                        </div>
                        {product?.discount > 0 && (
                          <span className="text-sm font-medium text-success">
                            Save {product?.discount}%
                          </span>
                        )}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </section>

        <RecommendationQuiz />
        <TrendingTopics />
        <SocialProof />
        <NewsletterSignup />
        <Footer />
      </div>
    </div>
  );
};

export default Homepage;